import React, { useState, useEffect } from 'react';
import {
    getScreensAPI,
    saveOrUpdateScreenAPI,
    resetScreenPasswordAPI,
    deleteScreenAPI,
} from "../../../../Component/Servicefile/campaignService";
// Screen Management Modal Component
const ScreenManagementModal = ({ isOpen, onClose, onSave, isSubmitting, initialData = null }) => {
    const [formData, setFormData] = useState({
        name: '',
        location: '',
        resolution: '',
        password: '',
        screenStatus: 'ACTIVE',
        screenOnTime: "00:00:00",
        screenOffTime: "00:00:00"
    });

    const [onTime, setOnTime] = useState({ hour: '00', minute: '00', second: '00' });
    const [offTime, setOffTime] = useState({ hour: '00', minute: '00', second: '00' });

    // Initialize form with initial data if editing
    useEffect(() => {
        if (initialData) {
            setFormData({
                name: initialData.name || '',
                location: initialData.location || '',
                resolution: initialData.resolution || '',
                password: '',
                screenStatus: initialData.screenStatus || 'ACTIVE',
                screenOnTime: initialData.screenOnTime || "00:00:00",
                screenOffTime: initialData.screenOffTime || "00:00:00"
            });

            // Parse time strings to set time pickers
            if (initialData.screenOnTime) {
                const [hour, minute, second] = initialData.screenOnTime.split(':');
                setOnTime({ hour: hour || '00', minute: minute || '00', second: second || '00' });
            }
            if (initialData.screenOffTime) {
                const [hour, minute, second] = initialData.screenOffTime.split(':');
                setOffTime({ hour: hour || '00', minute: minute || '00', second: second || '00' });
            }
        }
    }, [initialData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleStatusChange = (e) => {
        setFormData(prev => ({
            ...prev,
            screenStatus: e.target.value
        }));
    };

    const handleTimeChange = (type, field, value) => {
        // Ensure value is between 0-23 for hours and 0-59 for minutes/seconds
        let numValue = parseInt(value) || 0;

        if (field === 'hour') {
            numValue = Math.min(23, Math.max(0, numValue));
        } else {
            numValue = Math.min(59, Math.max(0, numValue));
        }

        const paddedValue = numValue.toString().padStart(2, '0');

        if (type === 'on') {
            const newTime = { ...onTime, [field]: paddedValue };
            setOnTime(newTime);
            // Format as HH:MM:SS string
            const timeString = `${newTime.hour}:${newTime.minute}:${newTime.second}`;
            setFormData(prev => ({
                ...prev,
                screenOnTime: timeString
            }));
        } else {
            const newTime = { ...offTime, [field]: paddedValue };
            setOffTime(newTime);
            // Format as HH:MM:SS string
            const timeString = `${newTime.hour}:${newTime.minute}:${newTime.second}`;
            setFormData(prev => ({
                ...prev,
                screenOffTime: timeString
            }));
        }
    };

    const handleSubmit = () => {
        // Validate required fields
        if (!formData.name || !formData.location || !formData.resolution) {
            alert('Please fill in all required fields');
            return;
        }

        // Create a clean copy of the data without any undefined values
        const submitData = {
            name: formData.name,
            location: formData.location,
            resolution: formData.resolution,
            screenStatus: formData.screenStatus,
            screenOnTime: formData.screenOnTime,
            screenOffTime: formData.screenOffTime
        };

        // Include ID if editing
        if (initialData?.id) {
            submitData.id = initialData.id;
            submitData.deviceId = initialData.deviceId;
        }

        // Only include password if it's provided (for new screens)
        if (formData.password && !initialData) {
            submitData.password = formData.password;
        }

        onSave(submitData, initialData ? 'update' : 'create');
    };

    const handleClose = () => {
        setFormData({
            name: '',
            location: '',
            resolution: '',
            password: '',
            screenStatus: 'ACTIVE',
            screenOnTime: "00:00:00",
            screenOffTime: "00:00:00"
        });
        setOnTime({ hour: '00', minute: '00', second: '00' });
        setOffTime({ hour: '00', minute: '00', second: '00' });
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div style={modalStyles.overlay}>
            <div style={modalStyles.modal}>
                {/* Header */}
                <div style={modalStyles.header}>
                    <h2 style={modalStyles.title}>{initialData ? '✏️ Edit Screen' : '🎯 Add New Screen'}</h2>
                    <button onClick={handleClose} style={modalStyles.closeButton} disabled={isSubmitting}>×</button>
                </div>

                {/* Content */}
                <div style={modalStyles.content}>
                    {/* Screen Name */}
                    <div style={modalStyles.section}>
                        <label style={modalStyles.label}>
                            Screen Name <span style={{ color: '#ff4444' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            placeholder="Enter screen name (e.g., Main Display, Kitchen Screen)"
                            style={modalStyles.input}
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Location */}
                    <div style={modalStyles.section}>
                        <label style={modalStyles.label}>
                            Location <span style={{ color: '#ff4444' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="location"
                            value={formData.location}
                            onChange={handleInputChange}
                            placeholder="Enter location (e.g., 1st Floor, Main Hall, Kitchen)"
                            style={modalStyles.input}
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Resolution */}
                    <div style={modalStyles.section}>
                        <label style={modalStyles.label}>
                            Resolution <span style={{ color: '#ff4444' }}>*</span>
                        </label>
                        <input
                            type="text"
                            name="resolution"
                            value={formData.resolution}
                            onChange={handleInputChange}
                            placeholder="Enter resolution (e.g., 1920x1080, 4K, etc.)"
                            style={modalStyles.input}
                            disabled={isSubmitting}
                        />
                    </div>
                    <label style={modalStyles.radioLabel}>
                        <input
                            type="radio"
                            name="screenStatus"
                            value="ACTIVE"
                            checked={formData.screenStatus === 'ACTIVE'}
                            onChange={handleStatusChange}
                            style={modalStyles.radio}
                            disabled={isSubmitting}
                        />
                        <span style={modalStyles.radioText}>Active</span>
                    </label>
                    <label style={modalStyles.radioLabel}>
                        <input
                            type="radio"
                            name="screenStatus"
                            value="INACTIVE"
                            checked={formData.screenStatus === 'INACTIVE'}
                            onChange={handleStatusChange}
                            style={modalStyles.radio}
                            disabled={isSubmitting}
                        />
                        <span style={modalStyles.radioText}>Inactive</span>
                    </label>
                    {/* Password - Only show for new screens */}
                    {!initialData && (
                        <div style={modalStyles.section}>
                            <label style={modalStyles.label}>Password</label>
                            <input
                                type="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="Enter screen password"
                                style={modalStyles.input}
                                disabled={isSubmitting}
                            />
                        </div>
                    )}

                    {/* Screen Status
                    <div style={modalStyles.section}>
                        <label style={modalStyles.label}>Screen Status</label>
                        <div style={modalStyles.radioGroup}>
                            <label style={modalStyles.radioLabel}>
                                <input
                                    type="radio"
                                    name="screenStatus"
                                    value="ACTIVE"
                                    checked={formData.screenStatus === 'ACTIVE'}
                                    onChange={handleStatusChange}
                                    style={modalStyles.radio}
                                    disabled={isSubmitting}
                                />
                                <span style={modalStyles.radioText}>Active</span>
                            </label>
                            <label style={modalStyles.radioLabel}>
                                <input
                                    type="radio"
                                    name="screenStatus"
                                    value="INACTIVE"
                                    checked={formData.screenStatus === 'INACTIVE'}
                                    onChange={handleStatusChange}
                                    style={modalStyles.radio}
                                    disabled={isSubmitting}
                                />
                                <span style={modalStyles.radioText}>Inactive</span>
                            </label>
                            <label style={modalStyles.radioLabel}>
                                <input
                                    type="radio"
                                    name="screenStatus"
                                    value="MAINTENANCE"
                                    checked={formData.screenStatus === 'MAINTENANCE'}
                                    onChange={handleStatusChange}
                                    style={modalStyles.radio}
                                    disabled={isSubmitting}
                                />
                                <span style={modalStyles.radioText}>Maintenance</span>
                            </label>
                        </div>
                    </div> */}

                    {/* Screen On Time */}
                    <div style={modalStyles.section}>
                        <label style={modalStyles.label}>Screen On Time (HH:MM:SS)</label>
                        <div style={modalStyles.timeContainer}>
                            <div style={modalStyles.timeField}>
                                <label style={modalStyles.timeLabel}>Hour (0-23)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="23"
                                    value={onTime.hour}
                                    onChange={(e) => handleTimeChange('on', 'hour', e.target.value)}
                                    style={modalStyles.timeInput}
                                    placeholder="00"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <span style={modalStyles.timeSeparator}>:</span>
                            <div style={modalStyles.timeField}>
                                <label style={modalStyles.timeLabel}>Minute (0-59)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={onTime.minute}
                                    onChange={(e) => handleTimeChange('on', 'minute', e.target.value)}
                                    style={modalStyles.timeInput}
                                    placeholder="00"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <span style={modalStyles.timeSeparator}>:</span>
                            <div style={modalStyles.timeField}>
                                <label style={modalStyles.timeLabel}>Second (0-59)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={onTime.second}
                                    onChange={(e) => handleTimeChange('on', 'second', e.target.value)}
                                    style={modalStyles.timeInput}
                                    placeholder="00"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <div style={modalStyles.timePreview}>
                            Preview: {formData.screenOnTime}
                        </div>
                    </div>

                    {/* Screen Off Time */}
                    <div style={modalStyles.section}>
                        <label style={modalStyles.label}>Screen Off Time (HH:MM:SS)</label>
                        <div style={modalStyles.timeContainer}>
                            <div style={modalStyles.timeField}>
                                <label style={modalStyles.timeLabel}>Hour (0-23)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="23"
                                    value={offTime.hour}
                                    onChange={(e) => handleTimeChange('off', 'hour', e.target.value)}
                                    style={modalStyles.timeInput}
                                    placeholder="00"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <span style={modalStyles.timeSeparator}>:</span>
                            <div style={modalStyles.timeField}>
                                <label style={modalStyles.timeLabel}>Minute (0-59)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={offTime.minute}
                                    onChange={(e) => handleTimeChange('off', 'minute', e.target.value)}
                                    style={modalStyles.timeInput}
                                    placeholder="00"
                                    disabled={isSubmitting}
                                />
                            </div>
                            <span style={modalStyles.timeSeparator}>:</span>
                            <div style={modalStyles.timeField}>
                                <label style={modalStyles.timeLabel}>Second (0-59)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="59"
                                    value={offTime.second}
                                    onChange={(e) => handleTimeChange('off', 'second', e.target.value)}
                                    style={modalStyles.timeInput}
                                    placeholder="00"
                                    disabled={isSubmitting}
                                />
                            </div>
                        </div>
                        <div style={modalStyles.timePreview}>
                            Preview: {formData.screenOffTime}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div style={modalStyles.footer}>
                    <button
                        onClick={handleClose}
                        style={modalStyles.cancelButton}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        style={{
                            ...modalStyles.submitButton,
                            opacity: !formData.name || !formData.location || !formData.resolution || isSubmitting ? 0.5 : 1,
                            cursor: !formData.name || !formData.location || !formData.resolution || isSubmitting ? 'not-allowed' : 'pointer'
                        }}
                        disabled={!formData.name || !formData.location || !formData.resolution || isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : (initialData ? 'Update Screen' : 'Add Screen')}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Modal Styles
const modalStyles = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
    },
    modal: {
        backgroundColor: 'white',
        borderRadius: '20px',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        animation: 'slideUp 0.3s ease'
    },
    header: {
        padding: '20px 25px',
        borderBottom: '2px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white'
    },
    title: {
        margin: 0,
        fontSize: '24px',
        fontWeight: '600'
    },
    closeButton: {
        background: 'none',
        border: 'none',
        fontSize: '32px',
        cursor: 'pointer',
        color: 'white',
        padding: '0',
        width: '40px',
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        transition: 'all 0.3s ease'
    },
    content: {
        padding: '25px',
        overflowY: 'auto',
        maxHeight: 'calc(90vh - 180px)'
    },
    section: {
        marginBottom: '25px'
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        fontWeight: '600',
        color: '#333',
        fontSize: '14px'
    },
    input: {
        width: '100%',
        padding: '12px 15px',
        borderRadius: '10px',
        border: '2px solid #e0e0e0',
        fontSize: '14px',
        transition: 'all 0.3s ease',
        outline: 'none',
        backgroundColor: 'white'
    },
    radioGroup: {
        display: 'flex',
        gap: '20px',
        flexWrap: 'wrap'
    },
    radioLabel: {
        display: 'flex',
        alignItems: 'center',
        cursor: 'pointer'
    },
    radio: {
        marginRight: '8px',
        width: '16px',
        height: '16px',
        cursor: 'pointer',
        accentColor: '#4CAF50'
    },
    radioText: {
        fontSize: '14px',
        color: '#333'
    },
    timeContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        flexWrap: 'wrap'
    },
    timeField: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    timeLabel: {
        fontSize: '12px',
        color: '#666',
        marginBottom: '4px'
    },
    timeInput: {
        width: '70px',
        padding: '8px',
        borderRadius: '8px',
        border: '2px solid #e0e0e0',
        fontSize: '14px',
        textAlign: 'center',
        outline: 'none'
    },
    timeSeparator: {
        fontSize: '20px',
        fontWeight: '600',
        color: '#666',
        marginTop: '15px'
    },
    timePreview: {
        marginTop: '8px',
        fontSize: '12px',
        color: '#666',
        fontFamily: 'monospace'
    },
    footer: {
        padding: '20px 25px',
        borderTop: '2px solid #f0f0f0',
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '15px'
    },
    cancelButton: {
        padding: '12px 25px',
        borderRadius: '10px',
        border: '2px solid #e0e0e0',
        backgroundColor: 'white',
        color: '#666',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.3s ease'
    },
    submitButton: {
        padding: '12px 25px',
        borderRadius: '10px',
        border: 'none',
        backgroundColor: '#4CAF50',
        color: 'white',
        fontSize: '14px',
        fontWeight: '500',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
    }
};

// Password Reset Modal Component
const PasswordResetModal = ({ isOpen, onClose, onReset, isSubmitting, deviceId }) => {
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    const handleSubmit = () => {
        if (!newPassword) {
            alert('Please enter a new password');
            return;
        }
        if (newPassword !== confirmPassword) {
            alert('Passwords do not match');
            return;
        }
        onReset(deviceId, newPassword);
    };

    if (!isOpen) return null;

    return (
        <div style={modalStyles.overlay}>
            <div style={modalStyles.modal}>
                <div style={modalStyles.header}>
                    <h2 style={modalStyles.title}>🔐 Reset Password</h2>
                    <button onClick={onClose} style={modalStyles.closeButton} disabled={isSubmitting}>×</button>
                </div>
                <div style={modalStyles.content}>
                    <div style={modalStyles.section}>
                        <label style={modalStyles.label}>Device ID</label>
                        <input
                            type="text"
                            value={deviceId || ''}
                            style={{ ...modalStyles.input, backgroundColor: '#f5f5f5' }}
                            disabled
                        />
                    </div>
                    <div style={modalStyles.section}>
                        <label style={modalStyles.label}>New Password</label>
                        <input
                            type="password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Enter new password"
                            style={modalStyles.input}
                            disabled={isSubmitting}
                        />
                    </div>
                    <div style={modalStyles.section}>
                        <label style={modalStyles.label}>Confirm Password</label>
                        <input
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            style={modalStyles.input}
                            disabled={isSubmitting}
                        />
                    </div>
                </div>
                <div style={modalStyles.footer}>
                    <button onClick={onClose} style={modalStyles.cancelButton} disabled={isSubmitting}>
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        style={{
                            ...modalStyles.submitButton,
                            opacity: !newPassword || !confirmPassword || isSubmitting ? 0.5 : 1,
                            cursor: !newPassword || !confirmPassword || isSubmitting ? 'not-allowed' : 'pointer'
                        }}
                        disabled={!newPassword || !confirmPassword || isSubmitting}
                    >
                        {isSubmitting ? 'Resetting...' : 'Reset Password'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// Main Foodscreens Component
const Foodscreens = ({ showScreenModal, setShowScreenModal, onSaveScreens, addedScreens }) => {
    // State to manage the list of screens - ensure it's always an array
    const [screens, setScreens] = useState(() => {
        // Initialize with addedScreens if provided
        if (addedScreens && Array.isArray(addedScreens)) {
            return addedScreens;
        }
        if (addedScreens && typeof addedScreens === 'object' && addedScreens !== null) {
            return [addedScreens];
        }
        return [];
    });

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // State for editing
    const [editingScreen, setEditingScreen] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);

    // State for password reset
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);

    // Get auth token from localStorage or context
    const getAuthToken = () => {
        // Try to get token from localStorage first
        const token = localStorage.getItem('authToken');
        if (token) {
            return token;
        }

        // If you're using cookies, you might need to include credentials
        return null;
    };

    // Get headers with authentication
    const getHeaders = (includeContentType = true) => {
        const headers = {};

        if (includeContentType) {
            headers['Content-Type'] = 'application/json';
        }

        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        return headers;
    };

    // Fetch screens on component mount
    useEffect(() => {
        fetchScreens();
    }, []);

    // Fetch screens from API
    const fetchScreens = async () => {
        try {
            setLoading(true);

            const screensData =
                await getScreensAPI(
                    getHeaders
                );

            setScreens(screensData);

            setError(null);

        } catch (err) {
            console.error(
                "Error fetching screens:",
                err
            );

            setError(
                err.message ||
                "Failed to load screens. Please try again."
            );

        } finally {
            setLoading(false);
        }
    };

    // Handle saving screens from modal (both create and update)
    const handleSaveScreens = async (
        screenData,
        action = "create"
    ) => {
        try {
            setIsSubmitting(true);

            const updatedScreen =
                await saveOrUpdateScreenAPI({
                    screenData,
                    action,
                    getHeaders,
                });

            let updatedScreens;

            if (action === "update") {
                updatedScreens =
                    screens.map((s) =>
                        s.id ===
                            updatedScreen.id
                            ? updatedScreen
                            : s
                    );
            } else {
                updatedScreens = [
                    ...screens,
                    updatedScreen,
                ];
            }

            setScreens(updatedScreens);

            if (
                onSaveScreens &&
                typeof onSaveScreens ===
                "function"
            ) {
                const screensToPass =
                    Array.isArray(
                        updatedScreens
                    )
                        ? updatedScreens
                        : [];

                setTimeout(() => {
                    try {
                        onSaveScreens(
                            screensToPass
                        );
                    } catch (callbackErr) {
                        console.error(
                            "Error in onSaveScreens callback:",
                            callbackErr
                        );
                    }
                }, 0);
            }

            alert(
                `Screen ${action === "update"
                    ? "updated"
                    : "added"
                } successfully!`
            );

            if (action === "update") {
                setShowEditModal(false);

                setEditingScreen(null);

            } else {
                setShowScreenModal(false);
            }

        } catch (err) {
            console.error(
                `Error ${action}ing screen:`,
                err
            );

            alert(
                err.message ||
                `Failed to ${action} screen. Please try again.`
            );

        } finally {
            setIsSubmitting(false);
        }
    };

    // Handle password reset
    const handlePasswordReset =
        async (
            deviceId,
            newPassword
        ) => {
            try {
                setIsSubmitting(true);

                await resetScreenPasswordAPI({
                    deviceId,
                    newPassword,
                    getHeaders,
                });

                alert(
                    "Password reset successfully!"
                );

                setShowPasswordModal(false);

                setSelectedDeviceId(null);

            } catch (err) {
                console.error(
                    "Error resetting password:",
                    err
                );

                alert(
                    err.message ||
                    "Failed to reset password. Please try again."
                );

            } finally {
                setIsSubmitting(false);
            }
        };

    // Handle delete screen
    const handleDeleteScreen =
        async (deviceId) => {
            if (
                window.confirm(
                    "Are you sure you want to delete this screen?"
                )
            ) {
                try {
                    setIsSubmitting(true);

                    await deleteScreenAPI({
                        deviceId,
                        getHeaders,
                    });

                    const updatedScreens =
                        screens.filter(
                            (screen) =>
                                screen.deviceId !==
                                deviceId
                        );

                    setScreens(
                        updatedScreens
                    );

                    if (
                        onSaveScreens &&
                        typeof onSaveScreens ===
                        "function"
                    ) {
                        const screensToPass =
                            Array.isArray(
                                updatedScreens
                            )
                                ? updatedScreens
                                : [];

                        onSaveScreens(
                            screensToPass
                        );
                    }

                    alert(
                        "Screen deleted successfully!"
                    );

                } catch (err) {
                    console.error(
                        "Error deleting screen:",
                        err
                    );

                    alert(
                        err.message ||
                        "Failed to delete screen. Please try again."
                    );

                } finally {
                    setIsSubmitting(false);
                }
            }
        };


    const handleEditClick = (screen) => {
        setEditingScreen(screen);
        setShowEditModal(true);
    };


    const handlePasswordResetClick = (deviceId) => {
        setSelectedDeviceId(deviceId);
        setShowPasswordModal(true);
    };


    const formatTime = (timeString) => {
        if (!timeString) return '00:00:00';
        return timeString;
    };

    if (loading) {
        return (
            <div style={styles.container}>
                <div style={styles.loadingContainer}>
                    <div style={styles.loadingSpinner}></div>
                    <p style={styles.loadingText}>Loading screens...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.container}>
                <div style={styles.errorContainer}>
                    <div style={styles.errorIcon}>⚠️</div>
                    <h3 style={styles.errorTitle}>Error Loading Screens</h3>
                    <p style={styles.errorText}>{error}</p>
                    <div style={styles.errorActions}>
                        <button onClick={fetchScreens} style={styles.retryButton}>
                            Retry
                        </button>
                        {error.includes('Unauthorized') && (
                            <button
                                onClick={() => window.location.href = '/login'}
                                style={styles.loginButton}
                            >
                                Go to Login
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={styles.container}>

            <div style={styles.header}>
                <h2 style={styles.title}>📺 Screen Management</h2>
                <button
                    onClick={() => setShowScreenModal(true)}
                    style={styles.addButton}
                    onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 15px rgba(76, 175, 80, 0.3)';
                    }}
                >
                    {/* <span style={styles.addIcon}>➕</span> */}
                    Add New Screen
                </button>
            </div>

            {/* Screens Grid */}
            {screens && screens.length > 0 ? (
                <div style={styles.screensGrid}>
                    {screens.map((screen) => (
                        <div key={screen.id} style={styles.screenCard}>
                            <div style={styles.screenHeader}>
                                <h3 style={styles.screenName}>{screen.name}</h3>
                                <div style={styles.actionButtons}>
                                    <button
                                        onClick={() => handleEditClick(screen)}
                                        style={styles.editButton}
                                        title="Edit Screen"
                                    >
                                        ✏️
                                    </button>
                                    <button
                                        onClick={() => handlePasswordResetClick(screen.deviceId)}
                                        style={styles.resetPasswordButton}
                                        title="Reset Password"
                                    >
                                        🔐
                                    </button>
                                    <button
                                        onClick={() => handleDeleteScreen(screen.deviceId)}
                                        style={styles.deleteButton}
                                        title="Delete Screen"
                                    >
                                        ×
                                    </button>
                                </div>
                            </div>
                            <div style={styles.screenDetails}>
                                <div style={styles.detailItem}>
                                    <span style={styles.detailLabel}>Device ID:</span>
                                    <span style={styles.detailValue}>{screen.deviceId || 'N/A'}</span>
                                </div>
                                <div style={styles.detailItem}>
                                    <span style={styles.detailLabel}>Location:</span>
                                    <span style={styles.detailValue}>{screen.location}</span>
                                </div>
                                <div style={styles.detailItem}>
                                    <span style={styles.detailLabel}>Resolution:</span>
                                    <span style={styles.detailValue}>{screen.resolution}</span>
                                </div>
                                <div style={styles.detailItem}>
                                    <span style={styles.detailLabel}>On Time:</span>
                                    <span style={styles.detailValue}>{formatTime(screen.screenOnTime)}</span>
                                </div>
                                <div style={styles.detailItem}>
                                    <span style={styles.detailLabel}>Off Time:</span>
                                    <span style={styles.detailValue}>{formatTime(screen.screenOffTime)}</span>
                                </div>
                                {screen.runningHours && (
                                    <div style={styles.detailItem}>
                                        <span style={styles.detailLabel}>Running Hours:</span>
                                        <span style={styles.detailValue}>{screen.runningHours}</span>
                                    </div>
                                )}
                            </div>
                            <div style={styles.screenStatus}>
                                <span style={{
                                    ...styles.statusBadge,
                                    backgroundColor:
                                        screen.screenStatus === 'ACTIVE' ? '#E8F5E9' :
                                            screen.screenStatus === 'INACTIVE' ? '#FFEBEE' : '#FFF3E0',
                                    color:
                                        screen.screenStatus === 'ACTIVE' ? '#4CAF50' :
                                            screen.screenStatus === 'INACTIVE' ? '#ff4444' : '#FF9800',
                                    borderColor:
                                        screen.screenStatus === 'ACTIVE' ? '#4CAF50' :
                                            screen.screenStatus === 'INACTIVE' ? '#ff4444' : '#FF9800'
                                }}>
                                    {screen.screenStatus}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div style={styles.emptyState}>
                    <div style={styles.emptyIcon}>📺</div>
                    <h3 style={styles.emptyTitle}>No Screens Added Yet</h3>
                    <p style={styles.emptyText}>Click the "Add New Screen" button to start adding screens to your restaurant.</p>
                </div>
            )}


            <ScreenManagementModal
                isOpen={showScreenModal}
                onClose={() => setShowScreenModal(false)}
                onSave={handleSaveScreens}
                isSubmitting={isSubmitting}
            />


            <ScreenManagementModal
                isOpen={showEditModal}
                onClose={() => {
                    setShowEditModal(false);
                    setEditingScreen(null);
                }}
                onSave={handleSaveScreens}
                isSubmitting={isSubmitting}
                initialData={editingScreen}
            />


            <PasswordResetModal
                isOpen={showPasswordModal}
                onClose={() => {
                    setShowPasswordModal(false);
                    setSelectedDeviceId(null);
                }}
                onReset={handlePasswordReset}
                isSubmitting={isSubmitting}
                deviceId={selectedDeviceId}
            />
        </div>
    );
};


const styles = {
    container: {
        padding: '20px',
        // background: 'linear-gradient(135deg, #667eea10 0%, #764ba210 100%)',
        borderRadius: '20px',
        minHeight: '500px',
        position: 'relative',
        overflow: 'hidden'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        padding: '0 10px',
        animation: 'slideDown 0.5s ease'
    },
    title: {
        margin: 0,
        fontSize: '32px',
        fontWeight: '700',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        textShadow: '2px 2px 4px rgba(0,0,0,0.1)',
        position: 'relative',
        paddingLeft: '15px',
        letterSpacing: '-0.5px'
    },
    addButton: {
        padding: '14px 28px',
        background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '50px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden',
        letterSpacing: '0.3px'
    },
    addIcon: {
        fontSize: '22px',
        filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.2))',
        animation: 'pulse 2s infinite'
    },
    screensGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '25px',
        padding: '10px',
        animation: 'fadeIn 0.8s ease'
    },
    screenCard: {
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '22px',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: '1px solid rgba(224, 224, 224, 0.5)',
        position: 'relative',
        animation: 'fadeIn 0.5s ease',
        backdropFilter: 'blur(10px)',
        cursor: 'pointer'
    },
    screenHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '15px',
        paddingBottom: '12px',
        borderBottom: '2px solid rgba(240, 240, 240, 0.8)',
        position: 'relative'
    },
    screenName: {
        margin: 0,
        fontSize: '18px',
        fontWeight: '600',
        color: '#4CAF50',
        position: 'relative',
        display: 'inline-block',
        paddingBottom: '2px'
    },
    actionButtons: {
        display: 'flex',
        gap: '8px',
        animation: 'slideIn 0.3s ease'
    },
    editButton: {
        background: 'none',
        border: 'none',
        fontSize: '18px',
        cursor: 'pointer',
        color: '#667eea',
        padding: '5px',
        borderRadius: '50%',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden'
    },
    resetPasswordButton: {
        background: 'none',
        border: 'none',
        fontSize: '16px',
        cursor: 'pointer',
        color: '#FF9800',
        padding: '5px',
        borderRadius: '50%',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden'
    },
    deleteButton: {
        background: 'none',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        color: '#ff4444',
        padding: '0',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        overflow: 'hidden'
    },
    screenDetails: {
        marginBottom: '15px',
        animation: 'slideUp 0.4s ease'
    },
    detailItem: {
        display: 'flex',
        marginBottom: '10px',
        fontSize: '14px',
        padding: '4px 8px',
        borderRadius: '6px',
        transition: 'background-color 0.2s ease'
    },
    detailLabel: {
        fontWeight: '600',
        color: '#666',
        width: '100px',
        position: 'relative'
    },
    detailValue: {
        color: '#333',
        flex: 1,
        fontWeight: '500'
    },
    screenStatus: {
        display: 'flex',
        justifyContent: 'flex-end',
        marginTop: '12px',
        animation: 'fadeIn 0.6s ease'
    },
    statusBadge: {
        padding: '6px 16px',
        borderRadius: '30px',
        fontSize: '12px',
        fontWeight: '600',
        border: '1px solid',
        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        transition: 'all 0.2s ease'
    },
    emptyState: {
        textAlign: 'center',
        padding: '70px 20px',
        backgroundColor: 'white',
        borderRadius: '20px',
        border: '2px dashed #e0e0e0',
        animation: 'fadeIn 0.8s ease',
        boxShadow: '0 10px 30px rgba(0, 0, 0, 0.05)'
    },
    emptyIcon: {
        fontSize: '80px',
        marginBottom: '20px',
        opacity: 0.5,
        animation: 'bounce 2s infinite'
    },
    emptyTitle: {
        fontSize: '22px',
        fontWeight: '600',
        color: '#333',
        marginBottom: '12px',
        letterSpacing: '-0.3px'
    },
    emptyText: {
        fontSize: '15px',
        color: '#666',
        maxWidth: '400px',
        margin: '0 auto',
        lineHeight: '1.6'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        animation: 'fadeIn 0.5s ease'
    },
    loadingSpinner: {
        width: '60px',
        height: '60px',
        border: '5px solid rgba(76, 175, 80, 0.1)',
        borderTop: '5px solid #4CAF50',
        borderRight: '5px solid #4CAF50',
        borderRadius: '50%',
        animation: 'spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite',
        marginBottom: '25px',
        boxShadow: '0 0 20px rgba(76, 175, 80, 0.2)'
    },
    loadingText: {
        fontSize: '16px',
        color: '#666',
        fontWeight: '500',
        letterSpacing: '0.3px',
        animation: 'pulse 1.5s infinite'
    },
    errorContainer: {
        textAlign: 'center',
        padding: '60px 20px',
        backgroundColor: 'white',
        borderRadius: '20px',
        border: '2px solid #ff4444',
        animation: 'shake 0.5s ease',
        boxShadow: '0 10px 30px rgba(255, 68, 68, 0.1)'
    },
    errorIcon: {
        fontSize: '56px',
        marginBottom: '20px',
        animation: 'pulse 1.5s infinite'
    },
    errorTitle: {
        fontSize: '22px',
        fontWeight: '600',
        color: '#ff4444',
        marginBottom: '12px',
        letterSpacing: '-0.3px'
    },
    errorText: {
        fontSize: '15px',
        color: '#666',
        marginBottom: '25px',
        lineHeight: '1.6'
    },
    errorActions: {
        display: 'flex',
        gap: '15px',
        justifyContent: 'center',
        animation: 'fadeIn 0.6s ease'
    },
    retryButton: {
        padding: '12px 35px',
        background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '50px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)',
        letterSpacing: '0.3px'
    },
    loginButton: {
        padding: '12px 35px',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '50px',
        fontSize: '15px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
        letterSpacing: '0.3px'
    }
};


const styleSheet = document.createElement("style");
styleSheet.innerText = `
    @keyframes fadeIn {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    
    @keyframes slideUp {
        from {
            transform: translateY(30px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }
    
    @keyframes slideDown {
        from {
            transform: translateY(-30px);
            opacity: 0;
        }
        to {
            transform: translateY(0);
            opacity: 1;
        }
    }
    
    @keyframes slideIn {
        from {
            transform: translateX(20px);
            opacity: 0;
        }
        to {
            transform: translateX(0);
            opacity: 1;
        }
    }
    
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
    
    @keyframes pulse {
        0%, 100% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.05);
        }
    }
    
    @keyframes bounce {
        0%, 100% {
            transform: translateY(0);
        }
        50% {
            transform: translateY(-10px);
        }
    }
    
    @keyframes shake {
        0%, 100% {
            transform: translateX(0);
        }
        10%, 30%, 50%, 70%, 90% {
            transform: translateX(-2px);
        }
        20%, 40%, 60%, 80% {
            transform: translateX(2px);
        }
    }
    
    @keyframes glow {
        0%, 100% {
            box-shadow: 0 0 5px rgba(76, 175, 80, 0.2);
        }
        50% {
            box-shadow: 0 0 20px rgba(76, 175, 80, 0.4);
        }
    }
    
    @keyframes float {
        0%, 100% {
            transform: translateY(0px);
        }
        50% {
            transform: translateY(-5px);
        }
    }
    
    // input:hover {
    //     border-color: #4CAF50 !important;
    //     box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1) !important;
    //     transform: translateY(-1px);
    // }
    
    // input:focus{
    //     border-color: #4CAF50 !important;
    //     box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.2) !important;
    //     outline: none;
    //     transform: translateY(-1px);
    // }
    
    .submit-button:hover:not(:disabled) {
        transform: translateY(-3px) scale(1.02);
        box-shadow: 0 8px 25px rgba(76, 175, 80, 0.4);
    }
    
    .submit-button:active:not(:disabled) {
        transform: translateY(-1px) scale(0.98);
    }
    
    .close-button:hover:not(:disabled) {
        background-color: rgba(255, 255, 255, 0.2);
        transform: rotate(90deg) scale(1.1);
    }
    
    .screen-card:hover {
        transform: translateY(-8px) scale(1.02);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.12);
        border-color: #4CAF50;
    }
    
    .screen-card:hover .screen-name {
        color: #45a049;
    }
    
    .edit-button:hover {
        background: linear-gradient(135deg, #667eea10 0%, #764ba210 100%);
        transform: scale(1.15) rotate(5deg);
        color: #5a6fd6;
    }
    
    .reset-password-button:hover {
        background: linear-gradient(135deg, #FF980010 0%, #F57C0010 100%);
        transform: scale(1.15) rotate(5deg);
        color: #F57C00;
    }
    
    .delete-button:hover {
        background: linear-gradient(135deg, #ff444410 0%, #cc000010 100%);
        transform: scale(1.15) rotate(5deg);
        color: #cc0000;
    }
    
    .detail-item:hover {
        background-color: rgba(76, 175, 80, 0.05);
        transform: translateX(5px);
    }
    
    .status-badge:hover {
        transform: scale(1.05);
        filter: brightness(1.1);
    }
    
    .retry-button:hover, .login-button:hover {
        transform: translateY(-3px) scale(1.02);
        box-shadow: 0 8px 25px currentColor;
    }
    
    .retry-button:active, .login-button:active {
        transform: translateY(-1px) scale(0.98);
    }
    
    .add-button::before {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 0;
        height: 0;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.2);
        transform: translate(-50%, -50%);
        transition: width 0.6s, height 0.6s;
    }
    
    .add-button:hover::before {
        width: 300px;
        height: 300px;
    }
    
    .modal-content {
        animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    .time-input:focus {
        animation: pulse 0.3s ease;
    }
    
    .empty-state:hover {
        border-color: #4CAF50;
        transform: scale(1.01);
    }
    
    .empty-icon {
        animation: float 3s ease-in-out infinite;
    }
    
    .loading-spinner {
        animation: spin 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) infinite, glow 2s ease-in-out infinite;
    }
    
    .action-button {
        position: relative;
        overflow: hidden;
    }
    
    .action-button::after {
        content: '';
        position: absolute;
        top: 50%;
        left: 50%;
        width: 5px;
        height: 5px;
        background: rgba(255, 255, 255, 0.5);
        opacity: 0;
        border-radius: 100%;
        transform: scale(1, 1) translate(-50%);
        transform-origin: 50% 50%;
    }
    
    .action-button:focus:not(:active)::after {
        animation: ripple 1s ease-out;
    }
    
    @keyframes ripple {
        0% {
            transform: scale(0, 0);
            opacity: 0.5;
        }
        20% {
            transform: scale(25, 25);
            opacity: 0.3;
        }
        100% {
            opacity: 0;
            transform: scale(40, 40);
        }
    }
    
    @media (max-width: 768px) {
        .screens-grid {
            grid-template-columns: 1fr;
        }
        
        .header {
            flex-direction: column;
            gap: 15px;
        }
        
        .title {
            font-size: 28px;
        }
    }
    
    @media (prefers-reduced-motion: reduce) {
        * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
        }
    }
`;
document.head.appendChild(styleSheet);

export default Foodscreens;